import { useCallback, useId, useMemo, useState, type MouseEvent } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import type { Swiper as SwiperType } from 'swiper';

import Carousel, { useCarousel } from '_components/Carousel';
import { isModifiedClick } from '_utils/GA4/clickTracking';
import { trackLearnStripCardClick } from '_utils/GA4/trackLearnStripEvent';
import { withLocalePrefix } from '@utils/breadcrumbs/withLocalePrefix';

import StoryCard from './StoryCard';
import type { LearnStripCard, LearnStripData } from './types';
import styles from './LearnStrip.module.scss';

const PRIORITY_CARD_COUNT = 2;

interface LearnStripInnerProps extends LearnStripData {
  headingId: string;
}

function LearnStripInner({
  heading,
  headingId,
  cards: authoredCards,
}: LearnStripInnerProps) {
  const { t } = useTranslation('common');
  const { asPath, locale } = useRouter();

  const cards = useMemo(
    () =>
      authoredCards.map((card) => ({
        ...card,
        cta: { ...card.cta, url: withLocalePrefix(card.cta.url, locale) },
      })),
    [authoredCards, locale]
  );
  const {
    state: { isScrollable },
  } = useCarousel();

  const [statusMessage, setStatusMessage] = useState('');

  const handleSlideChange = useCallback(
    (swiper: SwiperType) => {
      setStatusMessage(
        t('learnStrip.slideStatus', {
          current: swiper.activeIndex + 1,
          total: cards.length,
        })
      );
    },
    [cards.length, t]
  );

  const handleCardClick = useCallback(
    (
      card: LearnStripCard,
      index: number,
      event: MouseEvent<HTMLAnchorElement>
    ) => {
      const data = {
        interactionLocation: asPath.split('?')[0],
        linkUrl: card.cta.url,
        linkText: card.cta.label,
        carouselSlideName: card.heading,
        index,
      };

      if (isModifiedClick(event)) {
        trackLearnStripCardClick({ data });
        return;
      }

      event.preventDefault();
      trackLearnStripCardClick({
        data,
        eventCallback: () => {
          window.location.href = card.cta.url;
        },
      });
    },
    [asPath]
  );

  return (
    <>
      <div className={styles.header}>
        <h2 id={headingId} className={styles.heading}>
          {heading}
        </h2>

        {isScrollable && (
          <div className={styles.buttonGroup}>
            <Carousel.NavPrev
              aria-label={t('learnStrip.prev')}
              className={styles.navButton}
              iconSize={24}
            />
            <Carousel.NavNext
              aria-label={t('learnStrip.next')}
              className={styles.navButton}
              iconSize={24}
            />
          </div>
        )}
      </div>

      <div
        className={styles.track}
        role="group"
        aria-roledescription={t('learnStrip.carousel')}
        aria-label={heading}
      >
        <Carousel.Track
          className={styles.swiper}
          wrapperTag="ul"
          onSlideChange={handleSlideChange}
        >
          {cards.map((card, index) => (
            <Carousel.Slide
              key={`${card.heading}-${card.cta.url}`}
              tag="li"
              className={styles.slide}
            >
              <StoryCard
                {...card}
                priority={index < PRIORITY_CARD_COUNT}
                onClick={(event) => handleCardClick(card, index, event)}
              />
            </Carousel.Slide>
          ))}
        </Carousel.Track>
      </div>

      <p className={styles.status} role="status" aria-live="polite">
        {statusMessage}
      </p>
    </>
  );
}

const LearnStrip = ({ heading, cards }: LearnStripData) => {
  const headingId = useId();

  if (!heading || !cards?.length) return null;

  return (
    <section
      className={styles.root}
      aria-labelledby={headingId}
      data-testid="learn-strip"
    >
      <div className={styles.inner}>
        <Carousel.Provider>
          <LearnStripInner
            heading={heading}
            headingId={headingId}
            cards={cards}
          />
        </Carousel.Provider>
      </div>
    </section>
  );
};

export default LearnStrip;
